// src/components/shared/NumberInput.tsx

'use client'

import { useState, type ChangeEvent, type FocusEvent, useMemo } from 'react'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import { Tooltip } from '~/components/Tooltip'
import { formatInputValue, parseInputNumber } from '~/lib/utils/format/input'

/**
 * Props for the NumberInput component
 * 
 * @interface NumberInputProps
 * @property {string} id - The unique identifier for the input
 * @property {number} value - The current numeric value
 * @property {(value: number) => void} onChange - Handler called when the value changes
 * @property {string} label - The label text displayed above the input
 * @property {string} [description] - Optional descriptive text displayed below the input
 * @property {string} [placeholder] - Optional placeholder text when input is empty
 * @property {boolean} [isCurrency] - Whether to format the value as currency
 * @property {boolean} [showCents] - Whether to show cents in the formatted value
 * @property {number} [min] - Minimum allowed value
 * @property {number} [max] - Maximum allowed value
 * @property {number} [step] - Step increment for the input
 * @property {string} [tooltip] - Optional tooltip text
 * @property {string} [className] - Additional CSS classes
 * @property {boolean} [required] - Whether the input is required
 * @property {boolean} [disabled] - Whether the input is disabled
 * @property {'decimal' | 'numeric'} [inputMode] - Override the virtual-keyboard hint (defaults to decimal for currency, numeric otherwise)
 */
interface NumberInputProps {
  id: string
  value: number
  onChange: (value: number) => void
  /** Label text above the input. Pass an empty string to render no label (e.g. when the caller supplies its own external <label htmlFor>). */
  label: string
  description?: string
  placeholder?: string
  isCurrency?: boolean
  showCents?: boolean
  min?: number
  max?: number
  step?: number
  tooltip?: string
  className?: string
  required?: boolean
  disabled?: boolean
  inputMode?: 'decimal' | 'numeric'
}

/**
 * A custom input component for handling number inputs with formatting
 * Provides a natural typing experience while displaying formatted values
 * 
 * @param {NumberInputProps} props - Component properties
 * @returns {JSX.Element} Rendered component
 * 
 * @example
 * <NumberInput
 *   id="revenue"
 *   label="Total Revenue"
 *   value={100000}
 *   onChange={handleRevenueChange}
 *   isCurrency={true}
 *   showCents={false}
 *   min={0}
 * />
 */
export function NumberInput({
  id,
  value,
  onChange,
  label,
  description,
  placeholder = '0',
  isCurrency = false,
  showCents = true,
  min,
  max,
  step,
  tooltip,
  className = '',
  required = false,
  disabled = false,
  inputMode
}: NumberInputProps): JSX.Element {
  // Track input state for a more natural typing experience
  const [inputState, setInputState] = useState<'typing' | 'formatted'>('formatted')
  // Track the raw input value during typing
  const [typingValue, setTypingValue] = useState<string>('')
  
  // Format the display value based on the input state
  const displayValue = useMemo(() => {
    if (inputState === 'typing') {
      return typingValue
    }
    
    return formatInputValue(value, {
      isCurrency,
      minimumFractionDigits: (isCurrency && showCents) ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0
    })
  }, [value, inputState, typingValue, isCurrency, showCents])

  /**
   * Handles input focus event
   * Switches to typing mode and selects all text for easy editing
   * 
   * @param {FocusEvent<HTMLInputElement>} e - Focus event
   */
  const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
    // When focusing, show a clean version for editing
    setInputState('typing')
    setTypingValue(value.toString())
    // Select all text for easy replacement
    e.target.select()
  }
  
  /**
   * Handles input blur event
   * Parses the typed value, applies constraints, and updates the formatted display
   * 
   * @param {FocusEvent<HTMLInputElement>} _e - Blur event
   */
  const handleBlur = (_e: FocusEvent<HTMLInputElement>): void => {
    const parsedValue = parseInputNumber(typingValue)
    
    // Apply min/max constraints
    let constrainedValue = parsedValue
    if (min !== undefined && parsedValue < min) constrainedValue = min
    if (max !== undefined && parsedValue > max) constrainedValue = max
    
    // Update the actual value
    onChange(constrainedValue)
    // Switch back to formatted display
    setInputState('formatted')
  }
  
  /**
   * Handles input change events during typing
   * Validates input for number format and updates state
   * 
   * @param {ChangeEvent<HTMLInputElement>} e - Change event
   */
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const inputValue = e.target.value
    
    // Allow typing special characters needed for numbers
    if (inputValue === '' || 
        inputValue === '-' || 
        inputValue === '.' ||
        inputValue === '-.') {
      setTypingValue(inputValue)
      return
    }
    
    // Format validation using regex to allow only valid number patterns
    // This prevents non-numeric characters while allowing decimal points and negatives
    if (/^-?\d*\.?\d*$/.test(inputValue)) {
      setTypingValue(inputValue)
      
      // If it's a complete number, update the underlying value
      if (inputValue !== '-' && inputValue !== '.' && inputValue !== '-.') {
        const parsedValue = parseFloat(inputValue)
        if (!isNaN(parsedValue)) {
          onChange(parsedValue)
        }
      }
    }
  }

  return (
    <div className={`number-input ${className}`}>
      {(label || tooltip) && (
        <div className="flex items-center justify-between">
          {label && (
            <label
              htmlFor={id}
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          {tooltip && (
            <Tooltip content={tooltip}>
              <RiInformationLine className="h-4 w-4 text-gray-400" />
            </Tooltip>
          )}
        </div>
      )}
      <div className="mt-1 relative rounded-md shadow-sm">
        <input
          id={id}
          type="text"
          inputMode={inputMode ?? (isCurrency ? "decimal" : "numeric")}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          aria-required={required}
          aria-describedby={description ? `${id}-description` : undefined}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
        />
      </div>
      {description && (
        <p id={`${id}-description`} className="mt-1 text-sm text-gray-500">
          {description}
        </p>
      )}
    </div>
  )
}

export default NumberInput 